import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { z } from 'zod';
import type { LabelRevisionData } from '../../types/types';

dayjs.extend(customParseFormat);

export interface FieldConfig {
  name: string;
  type: string;
  required?: boolean;
  initialValue?: string;
  inputFormat?: string;
  allowedCharacters?: string;
  length?: number;
  fixedLength?: boolean;
  pickListValues?: string[];
  minimumValue?: string;
  maximumValue?: string;
}

const createDateValidator = () => {
  return (value: string) => {
    if (dayjs(value).isValid()) return true;
    const otherValidFormats = [
      'DD/MM/YYYY',
      'MM/DD/YYYY',
      'YYYY-MM-DD',
      'DD-MM-YYYY',
      'MM-DD-YYYY',
    ];
    for (const format of otherValidFormats) {
      if (dayjs(value, format, true).isValid()) {
        return true;
      }
    }
    return false;
  };
};

const createTimeValidator = () => {
  return (value: string) => {
    const formats = ['HH:mm', 'H:mm', 'HH:mm:ss', 'H:mm:ss', 'h:mm A'];
    for (const format of formats) {
      const time = dayjs(value, format, true);
      if (time.isValid()) return true;
    }
    return false;
  };
};

const createFieldSchema = ({
  name,
  type,
  required,
  allowedCharacters,
  length,
  fixedLength,
  pickListValues,
  minimumValue,
  maximumValue,
}: FieldConfig) => {
  const baseStringSchema = z.string({ error: `${name} is required` });

  const stringSchema = () => {
    let schema = required
      ? baseStringSchema.trim().min(1, `${name} is required`)
      : z.string().optional();
    if (allowedCharacters) {
      schema = schema.refine(
        (val) => !val || [...val].every((ch) => allowedCharacters.includes(ch)),
        { message: `${name} contains invalid characters` }
      );
    }
    if (fixedLength && length) {
      schema = schema.refine((val) => !val || val.length === length, {
        message: `${name} must be exactly ${length} characters`,
      });
    }
    if (pickListValues && pickListValues.length > 0) {
      schema = schema.refine((val) => !val || pickListValues.includes(val), {
        message: `${name} must be one of: ${pickListValues.join(', ')}`,
      });
    }
    return schema;
  };
  const numberSchema = () => {
    let schema = required
      ? z.coerce
          .number({ error: `${name} is required` })
          .refine((val) => val !== undefined && val !== null && !Number.isNaN(val), {
            message: `${name} is required`,
          })
      : z.coerce.number().optional();
    schema = schema.refine(
      (val) =>
        !val ||
        (typeof Number(val) === 'number' &&
          Number.isFinite(Number(val)) &&
          !Number.isNaN(Number(val))),
      { message: `${name} must be a valid number` }
    );
    if (minimumValue !== undefined) {
      schema = schema.refine((val) => !val || Number(val) >= Number(minimumValue), {
        message: `${name} must be at least ${minimumValue}`,
      });
    }
    if (maximumValue !== undefined) {
      schema = schema.refine((val) => !val || Number(val) <= Number(maximumValue), {
        message: `${name} must be at most ${maximumValue}`,
      });
    }
    if (fixedLength && length) {
      schema = schema.refine((val) => !val || String(Math.trunc(Number(val))).length === length, {
        message: `${name} must be exactly ${length} digits`,
      });
    }
    return schema;
  };
  const timeSchema = () => {
    const timeErrorMessage = `${name} must be a valid time`;
    const timeValidator = createTimeValidator();
    if (required) {
      return baseStringSchema
        .trim()
        .min(1, `${name} is required`)
        .refine(timeValidator, { message: timeErrorMessage });
    } else {
      return z
        .string()
        .optional()
        .refine((value) => !value || value.trim().length === 0 || timeValidator(value), {
          message: timeErrorMessage,
        });
    }
  };
  const dateSchema = () => {
    const dateValidator = createDateValidator();
    const dateErrorMessage = `${name} must be a valid date`;
    let schema = required
      ? baseStringSchema
          .trim()
          .min(1, `${name} is required`)
          .refine(dateValidator, { message: dateErrorMessage })
      : z
          .string()
          .optional()
          .refine((value) => !value || value.trim().length === 0 || dateValidator(value), {
            message: dateErrorMessage,
          });
    if (minimumValue) {
      schema = schema.refine(
        (val) =>
          !val || dayjs(val).isAfter(dayjs(minimumValue)) || dayjs(val).isSame(dayjs(minimumValue)),
        { message: `${name} must be on or after ${minimumValue}` }
      );
    }
    if (maximumValue) {
      schema = schema.refine(
        (val) =>
          !val ||
          dayjs(val).isBefore(dayjs(maximumValue)) ||
          dayjs(val).isSame(dayjs(maximumValue)),
        { message: `${name} must be on or before ${maximumValue}` }
      );
    }
    return schema;
  };

  switch (type) {
    case 'String':
      return stringSchema();
    case 'Double':
    case 'Currency':
      return numberSchema();
    case 'Time':
      return timeSchema();
    case 'Date':
      return dateSchema();
    default:
      console.error(`Unsupported field type: ${type} for field ${name}`);
      return z.string().optional();
  }
};

export const createLabelValidationSchema = (
  labelRevisionData: LabelRevisionData | null,
  fieldDisplayNames?: ReadonlyMap<string, string>
) => {
  if (!labelRevisionData?.fields) {
    return z.object({});
  }

  let schema = z.object({});

  Object.values(labelRevisionData.fields).forEach((fieldConfig) => {
    const config = fieldConfig as FieldConfig;
    const fieldName = config.name;
    const fieldSchema = createFieldSchema({
      name: fieldDisplayNames?.get(fieldName) ?? config.name,
      type: config.type,
      required: config.required || false,
      initialValue: config.initialValue,
      inputFormat: config.inputFormat,
      allowedCharacters: config.allowedCharacters,
      length: config.length,
      fixedLength: config.fixedLength,
      pickListValues: config.pickListValues,
      minimumValue: config.minimumValue,
      maximumValue: config.maximumValue,
    });
    schema = schema.extend({
      [fieldName]: fieldSchema,
    });
  });

  schema = schema.extend({
    quantity: z.coerce.number().refine(
      (val) => {
        const num = Number(val);
        return Number.isInteger(num) && num >= 1;
      },
      { message: 'Quantity must be at least 1' }
    ),
  });

  return schema.strict();
};

export const extractValidationErrors = (
  error: z.ZodError,
  fieldDisplayNames?: ReadonlyMap<string, string>
) => {
  const fieldErrors: Record<string, string> = {};
  const requiredErrors: string[] = [];
  const typeErrors: string[] = [];

  error.issues.forEach((err) => {
    const fieldName = err.path[0] as string;
    if (fieldName) {
      const displayName = fieldDisplayNames?.get(fieldName) ?? fieldName;
      fieldErrors[fieldName] = err.message;

      if (err.message.includes('required')) {
        requiredErrors.push(displayName);
      } else if (err.message.includes('valid date')) {
        typeErrors.push(`${displayName} (invalid date)`);
      } else {
        typeErrors.push(`${displayName} (invalid format)`);
      }
    }
  });

  let toastMessage = '';
  if (requiredErrors.length > 0 && typeErrors.length > 0) {
    toastMessage = `Required fields missing: ${requiredErrors.join(', ')}. Invalid format: ${typeErrors.join(', ')}.`;
  } else if (requiredErrors.length > 0) {
    toastMessage = `Required fields missing: ${requiredErrors.join(', ')}.`;
  } else if (typeErrors.length > 0) {
    toastMessage = `Invalid format for: ${typeErrors.join(', ')}.`;
  } else {
    toastMessage = 'Please fix the validation errors and try again.';
  }

  return {
    fieldErrors,
    toastMessage,
  };
};
